import { memo, useMemo } from 'react';

import { useShortIdContext } from '@air/provider-short-id';

import { GalleryView } from '~/components/v2/components/shared/gallery/GalleryView';
import {
  getPublicShowcaseListingSection,
  type PublicShowcaseListingBlockCategory,
} from '~/components/v2/components/showcase/publicShowcaseListingSection';
import { usePublicShowcaseGalleryNestedControls } from '~/components/v2/components/showcase/usePublicShowcaseGalleryNestedControls';
import { usePublicPermissionedVisibleCustomFieldsLength } from '~/components/v2/hooks/usePublicPermissionedVisibleCustomFieldsLength';
import { usePublicShowcaseContext } from '~/providers/PublicShowcaseProvider';
import { useScrollElementContext } from '~/providers/ScrollElementProvider';

export type { PublicShowcaseListingBlockCategory } from '~/components/v2/components/showcase/publicShowcaseListingSection';
export {
  getNestedChipHostCategory,
  getPublicShowcaseListingSection,
} from '~/components/v2/components/showcase/publicShowcaseListingSection';

export type PublicShowcaseListingBlockProps = {
  category: PublicShowcaseListingBlockCategory;
  /**
   * True for the one visible listing that should hold the folded chip. Each block is its own
   * gallery, so every one would otherwise look like the first section.
   */
  isNestedChipHost?: boolean;
};

// An empty listing block renders nothing — the block stack has no per-category null state. The
// component early-returns before mounting the gallery; this covers GalleryView's own isEmpty gate.
const PublicShowcaseListingBlockNullState = () => null;

// The gallery's selectable-items registration, deliberately doing nothing: see the prop's comment
// below. Module scope keeps the reference stable so GalleryView never re-runs it.
const useHostRegisteredSelectableItems = () => {};

export const PublicShowcaseListingBlock = memo(
  ({ category, isNestedChipHost = false }: PublicShowcaseListingBlockProps) => {
    const { shortId } = useShortIdContext();
    const { scrollElementRef } = useScrollElementContext();
    // The surface's card renderers, passed into the gallery view below: the viewer surfaces supply
    // the public cards, the editor's Edit tab its card twins (same bodies, menus reduced to
    // "Remove from link").
    const { renderAsset, renderBoard, renderFile, useGalleryViewItems } = usePublicShowcaseContext();
    // react-query dedupes the underlying boards/clips/files fetches across listing blocks — the
    // infinite queries are keyed on the shared shortId + view params, so N blocks share one fetch.
    const itemsData = useGalleryViewItems({ shortId });

    const section = getPublicShowcaseListingSection(itemsData.data, category);
    const { emptyStateHeader, renderHeader } = usePublicShowcaseGalleryNestedControls({
      isNestedChipHost,
    });

    // Zero out the other two categories so GalleryView's section filter drops them and this block
    // renders only its own. The loading/empty gates read the kept section so another category's
    // fetch state never leaks into this block.
    const maskedItemsData = useMemo(
      () => ({
        ...itemsData,
        data: {
          boards:
            category === 'boards'
              ? itemsData.data.boards
              : { ...itemsData.data.boards, hasMore: false, isLoading: false, items: [], total: 0 },
          clips:
            category === 'assets'
              ? itemsData.data.clips
              : { ...itemsData.data.clips, hasMore: false, isLoading: false, items: [], total: 0 },
          files:
            category === 'files'
              ? itemsData.data.files
              : { ...itemsData.data.files, hasMore: false, isLoading: false, items: [], total: 0 },
        },
        isEmpty: !section.isLoading && section.total <= 0,
        isLoading: section.isLoading && section.items.length === 0,
      }),
      [category, itemsData, section],
    );

    // `<= 0` rather than `=== 0`: a non-positive total is not a populated section, and it must not keep
    // an empty gallery (and its top margin) mounted above the sections that do have items.
    if (!section.isLoading && section.total <= 0) {
      return null;
    }

    return (
      <div data-category={category} data-testid="PUBLIC_COLLECTION_LISTING_BLOCK">
        <GalleryView
          emptyStateHeader={emptyStateHeader}
          itemsData={maskedItemsData}
          NullState={PublicShowcaseListingBlockNullState}
          renderAsset={renderAsset}
          renderBoard={renderBoard}
          renderFile={renderFile}
          renderHeader={renderHeader}
          scrollElementRef={scrollElementRef}
          // The public permission model's custom-field count — every public gallery states it.
          useCustomFieldsLength={usePublicPermissionedVisibleCustomFieldsLength}
          // A page renders one of these per listing block, and the selectable-items store holds ONE
          // list — so registration is the HOST's job (PublicShowcaseBlocks registers every block's
          // items together). A per-block registration would replace the combined list with just this
          // block's items and break shift-range selection across blocks.
          useSelectableItems={useHostRegisteredSelectableItems}
        />
      </div>
    );
  },
);

PublicShowcaseListingBlock.displayName = 'PublicShowcaseListingBlock';
